import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Menu, MenuItem, Typography, Avatar, Fade, Stack } from "@mui/material";
import ReactGA from "react-ga4";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Import flag images
import englandFlag from "../assets/england-flag.png";
import franceFlag from "../assets/france-flag.png";

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    handleClose();

    ReactGA.event({
      category: "User",
      action: "Change Language",
      label: lang,
    });
  };

  const currentLanguage = i18n.language || "fr";

  const languages = [
    { code: "en", name: "English", flag: englandFlag },
    { code: "fr", name: "Français", flag: franceFlag },
  ];

  const currentLangObj = languages.find(l => l.code === (currentLanguage.startsWith('en') ? 'en' : 'fr'));

  return (
    <Box>
      <Button
        id="language-button"
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon sx={{ color: '#9CA3AF' }} />}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          px: 2,
          py: 1,
          textTransform: 'none',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            borderColor: '#0FA3A3'
          }
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar 
            src={currentLangObj?.flag} 
            sx={{ width: 20, height: 20, borderRadius: '4px' }} 
            variant="square"
          />
          <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
            {currentLangObj?.name}
          </Typography>
        </Stack>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        sx={{
          '& .MuiPaper-root': {
            bgcolor: '#1F2937',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            mt: 1,
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            minWidth: '160px'
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem 
            key={lang.code} 
            onClick={() => handleLanguageChange(lang.code)}
            sx={{
              py: 1.5,
              px: 2.5,
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(15, 163, 163, 0.1)',
                color: '#0FA3A3'
              },
              ...(currentLanguage.startsWith(lang.code) && {
                bgcolor: 'rgba(15, 163, 163, 0.05)',
                color: '#0FA3A3'
              })
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <Avatar 
                src={lang.flag} 
                sx={{ width: 18, height: 18, borderRadius: '3px' }} 
                variant="square"
              />
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', flexGrow: 1 }}>
                {lang.name}
              </Typography>
              {currentLanguage.startsWith(lang.code) && (
                <Box sx={{ width: 6, height: 6, bgcolor: '#0FA3A3', borderRadius: '50%' }} />
              )}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default LanguageSwitcher;
